import { useCallback, useEffect, useState } from 'react'
import { CommonProperties } from '../common.interfaces'
import { LogsTableRow } from '../../beacon/common/beacon.interfaces'
import { useLoading } from '../../loading/LoadingContext'
import { useUserContext } from '../../user/UserContext'
import { DateTime } from 'luxon'
import { apiFetch, apiFetchRaw } from '../../utility/fetch'
import { API_DEV_URL, API_PROD_URL } from '../..'
import { Eye } from 'react-bootstrap-icons'
import { notify } from '../../notify/notify'
import JSONPretty from 'react-json-pretty'
import { CUSTOM_JSON_THEME } from '../common.constants'
import BlobModalComponent from '../../blobs/BlobModalComponent'
import { EnvironmentSelection } from './EnvironmentSelection'
import RefreshSvg from './svg/RefreshSvg'

interface ErrorLogsByFunctionAppProps extends CommonProperties {
  partitionKey: string
  environment: 'development' | 'production'
}

export function ErrorLogsByFunctionApp(props: ErrorLogsByFunctionAppProps) {
  const {toggleLoading, changeStep, changeSubStep} = useLoading()
  const {userData} = useUserContext()
  const [environment, setEnvironment] = useState<string>(props.environment)

  const [logs, setLogs] = useState<LogsTableRow[]>([])
  const [refresh, setRefresh] = useState<string>(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS))

  const [modalVisible, setModalVisible] = useState<boolean>(false)
  const [modalTitle, setModalTitle] = useState<string>(null)
  const [modalContent, setModalContent] = useState<any>(null)
  const [modalRaw, setModalRaw] = useState<string>(null)

  const refreshScreen = () => {
    setRefresh(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS))
  }

  const loadLogs = async () => {
    changeStep('Loading Logs Tables Rows')
    changeSubStep('Getting flexport function records from the logs table.')
    toggleLoading(true)
    const response = await apiFetch<LogsTableRow[]>({
      endpoint: 'azureTables',
      method: 'GET',
      accessToken: userData.accessToken,
      baseUrlOverride: environment === 'development' ? API_DEV_URL : API_PROD_URL,
      params: [
        {key: 'table', value: 'logs'},
        {key: 'filter', value: `PartitionKey eq '${props.partitionKey}'`}
      ]
    })
    setLogs(response.body)
    toggleLoading(false)
  }

  useEffect(() => {
    loadLogs()
  }, [environment, refresh, props.partitionKey])

  const handleViewErrorClicked = useCallback(async (title: string, path: string) => {
    const response = await apiFetchRaw({
      endpoint: 'blob',
      method: 'GET',
      accessToken: userData.accessToken,
      baseUrlOverride: environment === 'development' ? API_DEV_URL : API_PROD_URL,
      params: [
        {key: 'location', value: 'dataservices'},
        {key: 'container', value: 'storage'},
        {key: 'blobname', value: path}
      ],
      fileExtension: 'json'
    })
    if(!response.ok){
      console.log(response.body)
      notify('error', `Failed to get error content @ ${path}.`)
      toggleLoading(false)
      return
    }
    toggleLoading(false)
    setModalContent(<JSONPretty data={response.body as any} theme={CUSTOM_JSON_THEME} style={{fontSize: '1.5em'}} />)
    setModalRaw(JSON.stringify(response.body))
    setModalTitle(title)
    setModalVisible(true)
  }, [])

  return <div>
    <BlobModalComponent 
      blobData={modalContent}
      blobName={modalTitle}
      closeModal={() => setModalVisible(false)}
      modalVisible={modalVisible}
      raw={modalRaw}
    />
    {props?.isAdmin === true && <div>
      <div className='beacon-card'>
        <span className='beacon-header'>ADMINISTRATOR VIEW <Eye /></span>
        <br/>
        <span className='beacon-label'>Environment Selection</span>
        <br/>
        <EnvironmentSelection environment={environment} setEnvironment={setEnvironment} />
      </div>
    </div>}
    <div>
      <span onClick={refreshScreen} style={{cursor: 'pointer'}} title='Refresh the view.'><RefreshSvg height={25} /></span>&nbsp;
      <span className='beacon-label'>{logs.length} log(s) found for {props.partitionKey}.</span>
    </div>
    <table className='flexport-table'>
      <thead className='sticky-row-2'>
        <tr>
          <th>FUNCTION</th>
          <th>LAST ERROR</th>
          <th>ERROR WITHIN 4 DAYS</th>
        </tr>
      </thead>
      <tbody>
        {logs.map(l => {
          const logTime = DateTime.fromISO(l.timestamp)
          const isValid = logTime.isValid && logTime > DateTime.utc().minus({ days: 4 })
          return (
            <tr>
              <td>
                <span className='beacon-header'>{l.rowKey}</span>
              </td>
              <td>
                {DateTime.fromISO(l.timestamp).setZone().toFormat('MM/dd/yy t')}
              </td>
              <td>
                {isValid && <span title='View the error content.' style={{color: 'teal', cursor: 'pointer'}} onClick={() => {handleViewErrorClicked(`${l.rowKey} Error Content`, l.blobName)}}>
                  <Eye size={25} /> VIEW ERROR
                </span>}
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  </div>
}